// ListPage archetype — list/table pages (customers, orders, items, employees, payments)
// Composes: PageHeader + optional FilterBar slot + DataTable area + Empty/Error/Loading
// Usage: 30 ad-hoc lines → 5 lines
import {
  useCallback,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type UIEvent,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { PAGE_ACTION_FLOOR, PageActions, PageHeader } from '@heyroot/ui/primitives/PageHeader';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import { useActiveNavIdentity } from '../active-nav-identity-context';
import { PageFrame } from '../layout/PageFrame';

export interface ListPageProps {
  /** Page header */
  title: ReactNode;
  /** An enclosing workspace supplies the title; retain content-sized owner actions/content and render descriptions inline. */
  embeddedHeader?: boolean;
  description?: ReactNode;
  breadcrumbs?: ReactNode;
  /**
   * View pills rendered directly under the title row -- forwarded to
   * `PageHeader`'s own `tabs` slot. For a page with more than one table: show
   * one table at a time and switch between them here, so the locked header
   * never has to hold two tables (ADR-0188 D4, rule S2). Filter chips still
   * belong in `filters`.
   */
  tabs?: ReactNode;
  headerSize?: 'display' | 'heading';
  /**
   * How `description` renders -- forwarded straight through to `PageHeader`'s
   * own `descriptionMode`. Default `'inline'`: the description paragraph, clamped to two lines
   * with a "More" toggle only when it is actually cut (H6), full text always in the DOM.
   * See `PageHeader`'s own doc for the `'info'` behavior.
   */
  descriptionMode?: 'inline' | 'info';
  /**
   * Header band chrome. `'banded'` (default) -- today's `bg-surface
   * border-b border-border` card wrapping the header (+ any `summary`/
   * `context`). `'floating'` -- no background or border; the title sits
   * directly on the page's own background, matching the Catalog/Financial-
   * Audit-Trail redesign. Independent of `descriptionMode` -- a page can opt
   * into either, both, or neither.
   */
  headerChrome?: 'banded' | 'floating';
  /**
   * Primary action (ONE per view). Pass the control itself (a `Button`, or a link styled as one):
   * below `md` it is at least 44px tall and below `sm` it is the full-width row under the title.
   */
  primaryAction?: ReactNode;
  /**
   * Secondary actions (outline/ghost buttons), at most two. Pass the controls themselves, not a
   * wrapper around them: the 44px floor below `md` applies to each direct child of this slot.
   */
  secondaryActions?: ReactNode;
  /**
   * Summary rendered inside the header band, below the title — entity counts /
   * status breakdown / KPIs. Pass a `<SummaryStrip items={...} />` or a row of
   * `MetricCard`s. Surface a summary only
   * when the counts are already loaded or cheap to derive (DESIGN.md Part IV).
   */
  summary?: ReactNode;
  /**
   * One-line form of `summary` -- pass a `<SummaryStrip items={...} />` built from
   * the same data. Only used with `scroll="contained"` at <=1024px (tablet/phone):
   * once the rows scroll past 24px it replaces `summary`, and it restores at the
   * top or on tap. Phone (<640px) STARTS condensed and only expands on tap (with
   * a "Show less" to collapse again) -- full KPI cards there leave the rows no
   * height at all, so a scroll-driven swap could never fire (owner ruling
   * 2026-09-23). Desktop never condenses (locked-header owner ruling R1,
   * docs/plans/2026-09-23-locked-list-header-plan.md). Must not contain
   * interactive elements -- ListPage wraps it in the tap-to-expand button.
   */
  summaryCondensed?: ReactNode;
  /**
   * `'contained'` (default since rollout PR 10, ADR-0188 D4) -- the locked list header: title, summary, context and filters
   * stay put and only the rows scroll. The body becomes a bounded flex column;
   * the table inside it must own the scroll (`DataTable stickyHeader`), with
   * pagination pinned beside it rather than after the last row. Content with no
   * scroller of its own (a dashboard, a form, a calendar) scrolls in the body
   * instead, under the same fixed header -- the body is the fallback scroll box
   * (ADR-0188 D4, rule S6). A bounded table card never overflows the body, so
   * the fallback only engages where nothing else scrolls.
   * `'page'` -- opt-out only, with a one-line reason and a follow-up ticket (rollout PR 10).
   */
  scroll?: 'page' | 'contained';
  /**
   * Optional context/insight strip, rendered below the summary and above the filter
   * area (Command Deck vibrancy Slice 3.1 — archetype context strips). Pass freshness/
   * provenance chips or a single `<InsightCallout>` — never both a chip row and an
   * insight competing for attention. Omit entirely when there is nothing sourced,
   * specific, and actionable to show; an empty slot is not a placeholder to fill.
   */
  context?: ReactNode;
  /**
   * Opt-in: with `scroll="contained"`, `context` renders collapsed behind a
   * disclosure button with this label, and opens on tap. For a chart or panel
   * above the table that would otherwise eat the locked header's height (ADR-0188
   * D4, ruling (c), 2026-09-24). Ignored in page scroll -- `context` renders
   * inline there, as it does without the label.
   */
  contextLabel?: string;
  /** Filter area (search + filter chips, rendered below header) */
  filters?: ReactNode;
  /** Filter-row backing, independent of headerChrome. Defaults to the existing band. */
  filtersChrome?: 'banded' | 'floating';
  /** Main content (table, cards, etc.) */
  children: ReactNode;
  /** Shown when children would be empty */
  empty?: ReactNode;
  /** Shown on fetch error */
  error?: ReactNode;
  /** Shows skeleton loading state */
  loading?: boolean;
  /** Custom loading skeleton — overrides the default SkeletonTable when provided */
  loadingSkeleton?: ReactNode;
  /** Number of skeleton rows in the default skeleton, default 8 */
  loadingRowCount?: number;
  className?: string;
}

/** Tablet/phone range (DESIGN.md: tablet tops out at 1024px). */
const CONDENSE_QUERY = '(max-width: 1024px)';
/** Phone range (below Tailwind `sm`): condensed by default, see `summaryCondensed`. */
const PHONE_QUERY = '(max-width: 639px)';
/** Condense past this many px of row scroll; restore below RESTORE_AT (hysteresis). */
const CONDENSE_AFTER = 24;
const RESTORE_AT = 2;

const hasMatchMedia = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

function useMatchesQuery(query: string, enabled: boolean): boolean {
  // Read during render, not in an effect, so a phone never paints the full
  // summary for one frame before condensing -- including when `enabled` flips
  // true later (a data-gated `summary` arriving after mount).
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!enabled || !hasMatchMedia()) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query, enabled],
  );
  return useSyncExternalStore(
    subscribe,
    () => enabled && hasMatchMedia() && window.matchMedia(query).matches,
    () => false,
  );
}

/**
 * Height-animated show/hide for the summary swap. `inert` keeps the hidden side
 * out of the tab order and the accessibility tree (a collapsed MetricCard may
 * carry a drill link). `--duration-base` drops to 0ms under reduced motion.
 */
function Collapsible({ open, id, children }: { open: boolean; id?: string; children: ReactNode }) {
  return (
    <div
      id={id}
      inert={!open}
      className={[
        'grid transition-[grid-template-rows] duration-[var(--duration-base)] ease-out',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      ].join(' ')}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

export function ListPage({
  title,
  embeddedHeader = false,
  description,
  breadcrumbs,
  tabs,
  headerSize,
  descriptionMode = 'inline',
  headerChrome = 'banded',
  primaryAction,
  secondaryActions,
  summary,
  summaryCondensed,
  scroll = 'contained',
  context,
  contextLabel,
  filters,
  filtersChrome = 'banded',
  children,
  empty,
  error,
  loading,
  loadingSkeleton,
  loadingRowCount,
  className,
}: ListPageProps) {
  const navIdentity = useActiveNavIdentity();

  const condenseEligible = scroll === 'contained' && summary != null && summaryCondensed != null;
  const narrow = useMatchesQuery(CONDENSE_QUERY, condenseEligible);
  const canCondense = condenseEligible && narrow;
  const phone = useMatchesQuery(PHONE_QUERY, canCondense);
  const [condensed, setCondensed] = useState(false);
  // Phone is condensed unless the user tapped it open; tablet follows scroll.
  const [phoneExpanded, setPhoneExpanded] = useState(false);
  const showCondensed = canCondense && (phone ? !phoneExpanded : condensed);
  // The row scroller that last reported a scroll -- the tap-to-expand target.
  const scrollerRef = useRef<HTMLElement | null>(null);

  // Scroll events don't bubble but do capture, so the body wrapper sees the
  // table's own scroll region without any registration. Only vertical
  // position is read; horizontal table scrolls leave the summary alone.
  const onBodyScrollCapture = canCondense
    ? (event: UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        // Only the page's own rows drive the summary. React delivers scroll
        // from portaled children (drawers, dialogs) through this handler too,
        // and a page may render its drawer inline -- scrolling an open invoice
        // drawer must neither condense the summary nor become the tap-to-
        // expand scroll target.
        if (
          !event.currentTarget.contains(target) ||
          target.closest?.('[role="dialog"], [role="alertdialog"], [data-drawer]')
        ) {
          return;
        }
        scrollerRef.current = target;
        const top = target.scrollTop;
        if (phone) {
          // Never auto-restore on phone: back at the top, the full cards
          // would squeeze the rows to nothing again.
          if (top > CONDENSE_AFTER) setPhoneExpanded(false);
          return;
        }
        // Back near the top while condensed: restore only if the rows still
        // overflow. When they don't, this event is the browser clamping
        // scrollTop because condensing freed height -- not the user returning
        // to the top. Restoring then would re-hide the rows just scrolled to
        // and flicker on every further scroll; the tap still expands.
        const stillOverflows = target.scrollHeight - target.clientHeight >= RESTORE_AT;
        setCondensed((was) => (was ? top >= RESTORE_AT || !stillOverflows : top > CONDENSE_AFTER));
      }
    : undefined;

  // Ruling (c): contained pages start with the context collapsed so the rows
  // keep the height; the page-scroll layout never collapses it.
  const contextCollapsible = scroll === 'contained' && contextLabel != null;
  const [contextOpen, setContextOpen] = useState(false);
  const contextPanelId = useId();

  const contextNode = contextCollapsible ? (
    <div>
      <button
        type="button"
        aria-expanded={contextOpen}
        aria-controls={contextPanelId}
        onClick={() => setContextOpen((open) => !open)}
        className="inline-flex items-center gap-1.5 rounded-control text-body-sm text-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus"
      >
        <ChevronDown
          aria-hidden="true"
          className={[
            'h-4 w-4 shrink-0 transition-transform duration-[var(--duration-base)] motion-reduce:transition-none',
            contextOpen && 'rotate-180',
          ]
            .filter(Boolean)
            .join(' ')}
        />
        {contextLabel}
      </button>
      <Collapsible open={contextOpen} id={contextPanelId}>
        <div className="pt-3">{context}</div>
      </Collapsible>
    </div>
  ) : (
    context
  );

  const expandSummary = () => {
    setCondensed(false);
    setPhoneExpanded(true);
    scrollerRef.current?.scrollTo?.({ top: 0 });
  };

  const summaryNode = canCondense ? (
    <div>
      <Collapsible open={!showCondensed}>
        {summary}
        {phone && (
          <button
            type="button"
            onClick={() => setPhoneExpanded(false)}
            className="mt-2 text-body-sm text-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus"
          >
            Show less
          </button>
        )}
      </Collapsible>
      <Collapsible open={showCondensed}>
        <button
          type="button"
          aria-label="Show full summary"
          onClick={expandSummary}
          className="block w-full rounded-card text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus"
        >
          {summaryCondensed}
        </button>
      </Collapsible>
    </div>
  ) : (
    summary
  );

  // The page header's action grammar (H7, #16194): one primary and the secondaries beside it, a 44px
  // floor below `md`, and the primary a full-width row below `sm`. See `PageActions`.
  const actions =
    primaryAction || secondaryActions ? (
      <PageActions primary={primaryAction} secondary={secondaryActions} />
    ) : undefined;

  const headerSizeProps = headerSize ? { size: headerSize } : {};

  const body = loading
    ? (loadingSkeleton ?? (
        <table className="w-full">
          <tbody>
            <SkeletonTable rows={loadingRowCount ?? 8} />
          </tbody>
        </table>
      ))
    : error
      ? error
      : empty
        ? empty
        : children;

  return (
    <PageFrame
      mode="fluid"
      scroll={scroll}
      {...(className !== undefined ? { className } : {})}
      header={
        // No horizontal padding on the header/filter bands: PageFrame renders
        // header/children in the same zero-padding content box, so the root
        // frame's `--page-gutter` is the only horizontal inset -- keeps
        // header/body inline-start edges equal (plan §1.6/§7.5).
        // shrink-0: in contained scroll the header is a pinned flex sibling of
        // the body; it must never compress when the body fills.
        <div className="shrink-0">
          <div
            className={[headerChrome === 'banded' && 'bg-surface border-b border-border', 'py-4']
              .filter(Boolean)
              .join(' ')}
          >
            {embeddedHeader ? (
              <div className="flex flex-col gap-4">
                {breadcrumbs && <nav aria-label="Breadcrumb">{breadcrumbs}</nav>}
                {description && (
                  <div className="text-body-reading text-secondary">{description}</div>
                )}
                {actions && (
                  <div
                    className={[
                      'flex flex-wrap items-center justify-end gap-2',
                      PAGE_ACTION_FLOOR,
                    ].join(' ')}
                  >
                    {secondaryActions}
                    {primaryAction}
                  </div>
                )}
                {tabs}
              </div>
            ) : (
              <PageHeader
                title={title}
                description={description}
                descriptionMode={descriptionMode}
                breadcrumbs={breadcrumbs}
                actions={actions}
                tabs={tabs}
                divider={false}
                navIcon={navIdentity?.Icon}
                navColorVar={navIdentity?.colorVar}
                {...headerSizeProps}
              />
            )}
            {summary && <div className="mt-4">{summaryNode}</div>}
            {context && <div className="mt-4">{contextNode}</div>}
          </div>
          {filters && (
            <div
              data-slot="list-page-filters"
              className={[
                'py-3',
                filtersChrome === 'banded' && 'bg-surface-sunken border-b border-border',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {filters}
            </div>
          )}
        </div>
      }
    >
      {/* min-h-0 is inert in page scroll; in contained scroll it lets the body
          shrink so its table scrolls instead of overflowing the frame. In
          contained scroll the body is also the fallback scroll box (rule S6):
          content without its own scroller scrolls here instead of being
          clipped. overflow-y forces overflow-x off `visible`, so `-mx-1 px-1`
          leaves room for card shadows and focus rings without moving the
          content edge. onScrollCapture sees the body's own scroll too, so
          tablet/phone condensing works on these pages.
          Contained bodies keep an 8rem floor (min-h-32): on a short viewport
          (200% zoom on a laptop, WCAG 1.4.10) a header taller than the frame
          would otherwise squeeze the rows to 0px with nothing left to scroll;
          with the floor the page itself scrolls and the rows stay reachable.
          No normal-size screen measures under it (ADR-0188 PR B, 2026-09-30).
          print:overflow-visible: the app's print reset finds frame scroll boxes
          by `min-h-0`, which this body no longer carries, and a scroll box that
          stays overflow:auto in print loses its last rows on page 3+. */}
      <div
        className={[
          'flex flex-1 flex-col gap-4 py-4',
          scroll === 'contained'
            ? '-mx-1 min-h-32 overflow-y-auto px-1 print:overflow-visible'
            : 'min-h-0',
        ]
          .filter(Boolean)
          .join(' ')}
        onScrollCapture={onBodyScrollCapture}
      >
        {body}
      </div>
    </PageFrame>
  );
}
